export * from './helpers'

import { TDateTime, TDateTimeZ } from './helpers'

export interface ICountriesV1List {
  countryCode: string
  countryName: string
}

export interface ICurrenciesV1List {
  currencyCode: string
}

export enum ChannelType {
  Platform = 'PLATFORM',
  CSS = 'CSS',
  Extension = 'EXTENSION',
  Content = 'CONTENT',
}

export const channelTypeLabels: Record<ChannelType, string> = {
  [ChannelType.Platform]: 'Platform',
  [ChannelType.CSS]: 'CSS',
  [ChannelType.Extension]: 'Browser extension',
  [ChannelType.Content]: 'Content',
}

export interface IChannelsV2Channels {
  channelUuid: string
  channelDomain: string
  channelFaviconUrl: string
  channelIsActive: boolean
  channelName: string
  channelType: ChannelType
  channelScriptIsActive: boolean
  channelCampaignCount: number
  channelConnectedNetworksCount: number
  channelCreatedDatetime: TDateTime
  networks: {
    networkUuid: string
    networkName: string
  }[]
}

export interface IChannelsV1Channels {
  channelUuid: string
  channelDomain: string
  channelFaviconUrl: string
  channelIsActive: boolean
  channelName: string
  channelType: ChannelType
  channelScriptIsActive: boolean
  channelAdvertisersCount: number
  channelConnectedNetworksCount: number
  networks: {
    networkUuid: string
    networkName: string
  }[]
}

export interface IChannelsV1Channel {
  channelUuid: string
  channelDomain: string
  channelFaviconUrl: string
  channelIsActive: boolean
  channelName: string
  channelType: ChannelType
  channelCurrencyCode: string
  channelScriptIsActive: boolean
  channelAdvertisersCount: number
  channelConnectedNetworksCount: number
  channelCreatedDatetime: TDateTime
}

export interface IChannelsV1List {
  channelUuid: string
  channelName: string
  channelDomain: string
  channelFaviconUrl: string
  channelType: ChannelType
}

export interface INetworksV1Networks {
  networkUuid: string
  networkName: string
  networkLogoUrl: string
  networkIdentifier: string
  networkUrl: string
  networkSupportsApiPaidConversions: boolean
  networkSupportsApiChannels: boolean
  networkSupportsApiConversions: boolean
  networkSupportsApiPrograms: boolean
  networkSupportsApiRejections: boolean
  networkSupportsCallbacks: boolean
  networkSupportsGlobalChannel: boolean
  networkConnectionApiKeyIsRequired: boolean
  networkConnectionApiPasswordIsRequired: boolean
  networkConnectionApiUsernameIsRequired: boolean
  networkConnectionApiPartnerIdIsRequired: boolean
  networkConnectionApiCountryCodeIsRequired: boolean
  networkConnectionCpcCurrencyCodeIsRequired: boolean
  networkConnectionCpcValueIsRequired: boolean
  networkConnectionApiCountryCodeText: string
  networkConnectionApiKeyText: string
  networkConnectionApiPartnerIdText: string
  networkConnectionApiPasswordText: string
  networkConnectionApiUsernameText: string
  networkConnectionCpcCurrencyCodeText: string
  networkConnectionCpcValueText: string
}

export interface INetworksV1Network {
  features: {
    apiPaidConversions: boolean
    apiChannels: boolean
    apiConversions: boolean
    apiPrograms: boolean
    apiRejections: boolean
    callbacks: boolean
    apiCpc: boolean
  }
  network: {
    networkUuid: string
    networkName: string
    networkLogoUrl: string
    networkUrl: string
  }
  connections: {
    connectionUuid: string
    connectionName: string
    connectionApiPartnerId: string
    connectionIsActive: boolean
    connectionIsFallback: boolean
    connectionHadErrors: boolean
    connectionCallbackStatus: string
    connectionCreatedDatetime: TDateTime
    connectionUpdatedDatetime: TDateTime
    connectionChannels: {
      connectionChannelUuid: string
      connectionChannelName: string
      connectionChannelUrl: string
      connectionsMappings: {
        connectionMappingUuid: string
        connectionMappingChannelUuid: string
        channel: {
          channelUuid: string
          channelName: string
          channelUrl: string
        }
      }[]
    }[]
  }[]
}

export interface INetworksV1List {
  networkUuid: string
  networkName: string
  networkLogoUrl: string
}

export interface INetworksV2Networks {
  networkUuid: string
  networkName: string
  networkLogoUrl: string
  networkIdentifier: string
  networkUrl: string
  networkSupportsApiPaidConversions: boolean
  networkSupportsApiChannels: boolean
  networkSupportsApiConversions: boolean
  networkSupportsApiPrograms: boolean
  networkSupportsApiRejections: boolean
  networkSupportsCallbacks: boolean
  networkSupportsGlobalChannel: boolean
  networkConnectionApiKeyIsRequired: boolean
  networkConnectionApiPasswordIsRequired: boolean
  networkConnectionApiUsernameIsRequired: boolean
  networkConnectionApiPartnerIdIsRequired: boolean
  networkConnectionApiCountryCodeIsRequired: boolean
  networkConnectionCpcCurrencyCodeIsRequired: boolean
  networkConnectionCpcValueIsRequired: boolean
  // naming
  networkConnectionApiCountryCodeText: string
  networkConnectionApiKeyText: string
  networkConnectionApiPartnerIdText: string
  networkConnectionApiPasswordText: string
  networkConnectionApiUsernameText: string
  networkConnectionCpcCurrencyCodeText: string
  networkConnectionCpcValueText: string
  // additional
  networkIsConnected: boolean
  networkIsCustom: boolean
  networkHasOpportunities: boolean
  networkHasAlternatives: boolean
}

export interface INetworksV1Connections {
  connectionCallbackStatus: string
  connectionCreatedDatetime: string
  connectionIsActive: boolean
  connectionIsFallback: boolean
  connectionName: string
  connectionPartnerId: string
  connectionUpdatedDatetime: string
  connectionUuid: string
  network: {
    networkLogoUrl: string
    networkName: string
    networkUuid: string
  }
}

export interface IInsightsV1Click {
  clickUuid: string
  clickDatetime: TDateTime
  clickUrlPath: string
  clickNetworkUrl: string
  clickReferer: string
  clickTargetUrlPath: string
  clickTargetUrl: string
  clickDeviceType: string
  clickUrlShortCode: string
  clickSubId: string
  clickSubId1: string
  clickSubId2: string
  clickSubId3: string
  clickSubId4: string
  clickSubId5: string
  clickTags: any
  clickName: string
  clickCustomTags: string
  advertiser: {
    advertiserUuid: string
    advertiserDomain: string
  }
  network: {
    networkUuid: string
    networkName: string
    networkLogoUrl: string
    networkUrl: string
  }
  connection: {
    connectionUuid: string
    connectionPartnerId: string
  }
  channel: {
    channelUuid: string
    channelDomain: string
  }
  tracker: {
    trackerCommissionFlat: number
    trackerCommissionPercentage: number
    trackerCommissionType: string
    trackerCurrencyCode: string
    trackerName: string
  }
  conversions: {
    conversionUuid: string
    conversionClickUuid: string
    conversionName: string
    conversionCommission: number
    conversionCurrencyCode: string
    conversionDatetime: TDateTime
    conversionCreatedDatetime: TDateTime
    conversionPaidDatetime: TDateTime
    conversionUpdatedDatetime: TDateTime
    conversionRejectedDatetime: TDateTime
    conversionSaleOrderId: string
    conversionDeviceType: string
    conversionReferer: string
    conversionStatusKey: string
    conversionErrorCode: string
    conversionUrlShortCode: string
    conversionNetworkTransactionId: string
    conversionSale: string
    conversionUrlPath: string
    conversionTargetUrlPath: string
    conversionSubId: string
    conversionSubId1: string
    conversionSubId2: string
    conversionSubId3: string
    conversionSubId4: string
    conversionSubId5: string
  }[]
}

export interface IInsightsV1Conversion {
  click: {
    clickDatetime: string
  }
  advertiser: {
    advertiserUuid: string
    advertiserDomain: string
  }
  network: {
    networkUuid: string
    networkName: string
    networkLogoUrl: string
    networkUrl: string
  }
  connection: {
    connectionUuid: string
    connectionPartnerId: string
  }
  channel: {
    channelUuid: string
    channelDomain: string
  }
  tracker: {
    trackerCommissionFlat: number
    trackerCommissionPercentage: number
    trackerCommissionType: string
    trackerCurrencyCode: string
    trackerName: string
  }
  conversionUuid: string
  conversionClickUuid: string
  conversionCommission: number
  conversionCurrencyCode: string
  conversionName: string
  conversionDatetime: TDateTime
  conversionCreatedDatetime: TDateTime
  conversionPaidDatetime: TDateTime
  conversionUpdatedDatetime: TDateTime
  conversionRejectedDatetime: TDateTime
  conversionSaleOrderId: string
  conversionDeviceType: string
  conversionReferer: string
  conversionStatusKey: string
  conversionErrorCode: string
  conversionUrlShortCode: string
  conversionNetworkTransactionId: string
  conversionSale: number
  conversionUrlPath: string
  conversionTargetUrlPath: string
  conversionSubId: string
  conversionSubId1: string
  conversionSubId2: string
  conversionSubId3: string
  conversionSubId4: string
  conversionSubId5: string
  conversionTags: any
  conversionCustomTags: any
}

export interface IProfileV1Users {
  userUuid: string
  userFirstname: string
  userLastname: string
  userEmail: string
  userTitle: string
  userUpdatedDatetime: TDateTime
  userLoginDatetime: TDateTime
  userPhoneCountryCode: string
  userPhone: string
  userCurrencyCode: string
}

export type IInsightsV1StatisticsRow = {
  grp: string
  name: string
  conversionCount: number
  rejectedConversionCount: number
  cpcCount: number
  conversionCommission: number
  rejectedConversionCommission: number
  cpcCommission: number
  conversionTotalCommission: number
  conversionPendingCommission: number
  conversionRejectedCommission: number
  conversionUnboostedCommission?: number
  conversionSale: number
  conversionRejectedSale: number
  conversionDatetime: TDateTimeZ
  conversionNetworkUuids: string
  conversionTrackerUuids: string
  clickCount: number
  clickNetworkUuids: string
  clickTrackerUuids: string
  pageviewCount: number

  prev?: IInsightsV1StatisticsRow
}

export interface IInsightsV1Statistics {
  rows: IInsightsV1StatisticsRow[]
  totals: {
    pageviewCount: number
    clicksCount: number
    conversionTotalCommission: number
    conversionPendingCommission: number
    conversionRejectedCommission: number
    conversionSale: number
    conversionRejectedSale: number
    conversionCount: number
    rejectedConversionCount: number
    cpcCount: number
    conversionCommission: number
    rejectedConversionCommission: number
    cpcCommission: number
  }
}

export interface IInsightsV1FavoriteStatistics {
  favoriteUuid?: string
  favoriteGroupBy?: string
  favoriteGroupName?: string
}

// ---------------------------------------------------------------------------
// Anomaly detection (v2)
// ---------------------------------------------------------------------------

export type TAnomaliesDevice = 'Mobile' | 'Desktop'
export type TAnomaliesAlertFamily = 'drift' | 'rejection' | 'acute'
export type TAnomaliesPrimaryMetric =
  | 'cr'
  | 'epc'
  | 'spc'
  | 'clicks'
  | 'conversions'
  | 'rejection_rate'
export type TAnomaliesStatus = 'new' | 'worsening' | 'continuing'

export interface IAnomaliesV2DatesResponse {
  latestDate: string
  minDate: string
  maxDate: string
  detectorVersion: string
}

export interface IAnomaliesV2Summary {
  date: string
  detectorVersion: string
  activeAlerts: number
  newAlerts: number
  worseningAlerts: number
  // Currently-suppressed alert count for the selected date, always returned
  // regardless of the requested mode. Drives the "+N suppressed" badge.
  suppressedAlerts?: number
  families: { drift: number; rejection: number; acute: number }
  devices: { Mobile: number; Desktop: number }
}

// View mode for the suppression-aware endpoints (/alerts, /summary, /timeline,
// and the rejection-review trio). "exclude" hides suppressed rows (default);
// "only" shows only those rows; "include" shows everything regardless.
export type TAnomaliesSuppressedMode = 'exclude' | 'only' | 'include'

// What a single suppression row targets. A metric value suppresses only daily
// alerts whose primaryMetric matches; 'rejections' hides the advertiser from
// the Monthly Rejection Review (daily alerts unaffected); null/omitted
// suppresses every daily alert (the pre-scoping behavior).
export type TAnomaliesSuppressionScope =
  | 'cr'
  | 'epc'
  | 'spc'
  | 'clicks'
  | 'conversions'
  | 'rejections'

// Suppression record returned by /anomalies/v2/suppressions.
export interface IAnomaliesV2Suppression {
  uuid: string
  advertiserDomain: string
  // null = all daily alerts. One row = one scope; an advertiser with several
  // scoped suppressions appears once per scope.
  primaryMetric: TAnomaliesSuppressionScope | null
  createdByUserUuid: string
  createdByUserFirstname: string | null
  createdByUserLastname: string | null
  createdAt: string
  expiresAt: string | null
  deletedAt: string | null
  reason: string | null
}

export interface IAnomaliesV2SuppressionsResponse {
  suppressions: IAnomaliesV2Suppression[]
}

// Per-user saved filter preset. One default per (user, scope); the backend
// auto-clears the previous default when a new one is set. The `values` field
// is a free-form bag the backend persists verbatim — convention is to put
// exactly the querystring params the data endpoints accept.
export type TAnomaliesV2UserFilterScope = 'triage' | 'rejection_review'

export interface IAnomaliesV2UserFilter {
  uuid: string
  scope: TAnomaliesV2UserFilterScope
  name: string
  isDefault: boolean
  values: Record<string, string | number>
  createdAt: string
  updatedAt: string
}

export interface IAnomaliesV2UserFiltersResponse {
  filters: IAnomaliesV2UserFilter[]
}

export interface IAnomaliesV2Alert {
  rank: number
  eventDate: string
  advertiserDomain: string
  device: TAnomaliesDevice
  alertFamily: TAnomaliesAlertFamily
  primaryMetric: TAnomaliesPrimaryMetric
  displayReason: string
  reasonText: string
  currentValue: number | null
  baselineValue: number | null
  percentChange: number | null
  estimatedLossLabel: string | null
  priorityScore: number
  isFirstAlertDay: boolean
  isWorseningToday: boolean
  alertFirstSeenDate: string
  alertDaysActive: number
  clicks30d?: number
  sale30dDkk?: number
  commission30dDkk?: number
  isSeasonal?: boolean
}

export interface IAnomaliesV2AlertsResponse {
  date: string
  page: number
  pageSize: number
  total: number
  // Alerts that would show today under "exclude" mode but are currently
  // hidden. Used for the "+N suppressed" badge above the triage list.
  suppressedCount?: number
  alerts: IAnomaliesV2Alert[]
}

// One day of the per-advertiser/device time series. Most fields are optional
// because the detail endpoint only populates the ones relevant to the alert
// family (drift => 30d/anchor ratios, acute => 7v30/7v90 z-scores, etc).
export interface IAnomaliesV2SeriesPointNetworkRef {
  uuid: string
  name: string
}

export interface IAnomaliesV2Network {
  uuid: string
  name: string
  activeDays: number
  firstSeen: string
  lastSeen: string
}

export interface IAnomaliesV2SeriesPoint {
  eventDate: string
  clicks?: number
  conversions?: number
  rejectedConversions?: number
  saleDkk?: number
  commissionDkk?: number
  // Networks active for this row's (eventDate, device). Empty array = no
  // recorded networks (typically missing-data days).
  networks?: IAnomaliesV2SeriesPointNetworkRef[]
  // 7-day rolling
  cr7d?: number | null
  epc7d?: number | null
  spc7d?: number | null
  rejRate7d?: number | null
  // 30-day current window
  cr30dCurr?: number | null
  epc30dCurr?: number | null
  spc30dCurr?: number | null
  rejRate30dBase?: number | null
  // 6-month anchor (drift)
  cr180dAnchor?: number | null
  epc180dAnchor?: number | null
  spc180dAnchor?: number | null
  // Drift ratios (current / anchor)
  crDriftRatio?: number | null
  epcDriftRatio?: number | null
  spcDriftRatio?: number | null
  // Acute z-scores
  cr7v30Z?: number | null
  cr7v90Z?: number | null
  epc7v30Z?: number | null
  epc7v90Z?: number | null
  spc7v30Z?: number | null
  spc7v90Z?: number | null
  clicks7v30Z?: number | null
  clicks7v90Z?: number | null
  conversions7v30Z?: number | null
  conversions7v90Z?: number | null
  rejRate7v30Z?: number | null
  // 7-day rolling counts for clicks/conversions — used by the primary chart
  // when the alert metric is one of these volume series.
  clicks7d?: number | null
  conversions7d?: number | null
  // 30-day current totals for clicks/conversions.
  clicks30dCurr?: number | null
  conversions30dCurr?: number | null
  // Reported-date rejection volume — the new rejection signal. Counts are
  // bucketed by the day the rejection was *recorded* (not by the day of the
  // underlying conversion), so bulk-reconciliation events spike on arrival
  // day rather than retroactively perturbing the origin period.
  reportedRejections?: number | null
  reportedRej7d?: number | null
  reportedRej30dBase?: number | null
  reportedRej90dBase?: number | null
  reportedRej7v30Z?: number | null
  reportedRej7v90Z?: number | null
}

// Lightweight alert shape used on related-alerts lists and the per-advertiser
// history table. Subset of IAnomaliesV2Alert, but device + family + metric
// are always present because that's the primary nav signal.
export interface IAnomaliesV2RelatedAlert {
  eventDate: string
  device: TAnomaliesDevice
  alertFamily: TAnomaliesAlertFamily
  primaryMetric: TAnomaliesPrimaryMetric
  displayReason?: string
  reasonText?: string
  priorityScore?: number
  isFirstAlertDay?: boolean
  isWorseningToday?: boolean
  alertFirstSeenDate?: string
  alertDaysActive?: number
}

// Resolved selection echoed back by the detail endpoint. The frontend may
// send some/all of these as request params; backend fills in defaults so the
// page is usable from a bare /anomalies/:domain link.
export interface IAnomaliesV2DetailSelected {
  advertiserDomain: string
  date: string
  days: number
  device: TAnomaliesDevice
  metric: TAnomaliesPrimaryMetric
}

export interface IAnomaliesV2DetailAvailable {
  devices: TAnomaliesDevice[]
  metrics: TAnomaliesPrimaryMetric[]
  minDate: string
  maxDate: string
}

export interface IAnomaliesV2DetailResponse {
  selected?: IAnomaliesV2DetailSelected
  available?: IAnomaliesV2DetailAvailable
  // Legacy fields — kept for backward compatibility with the old shape.
  advertiserDomain?: string
  device?: TAnomaliesDevice
  selectedDate?: string
  selectedAlert?: IAnomaliesV2Alert | null
  // Series may include multiple devices when the advertiser has both.
  // Each row carries `device` so the frontend can filter.
  series: IAnomaliesV2SeriesPoint[]
  sameDateAlerts?: IAnomaliesV2RelatedAlert[]
  nearbyAlerts?: IAnomaliesV2RelatedAlert[]
  // Full historical alert list for the advertiser within the selected range.
  alertHistory?: IAnomaliesV2RelatedAlert[]
  // Cross-window network summary, sorted by activeDays desc.
  networks?: IAnomaliesV2Network[]
}

// Daily aggregate above the triage list. activeAlerts is the row count for
// that day; newAlerts/worseningAlerts split it; family counts give the
// stacked-by-family view.
export interface IAnomaliesV2TimelineRow {
  eventDate: string
  activeAlerts: number
  // Always-on count of currently-hidden rows on that day, regardless of
  // requested mode. Lets us overlay a faint "suppressed" series on the
  // graph without re-fetching.
  suppressedAlerts?: number
  newAlerts: number
  worseningAlerts: number
  sumPriorityScore: number
  maxPriorityScore: number
  driftAlerts: number
  rejectionAlerts: number
  acuteAlerts: number
}

export interface IAnomaliesV2TimelineResponse {
  rows: IAnomaliesV2TimelineRow[]
}

export interface IAnomaliesV2SearchRow {
  advertiserDomain: string
  latestAlertDate: string | null
  totalAlertDays: number
  maxPriority: number
}

export interface IAnomaliesV2SearchResponse {
  rows: IAnomaliesV2SearchRow[]
}

// ============================================================================
// Monthly rejection review — /anomalies/v2/rejection-review/{summary,list,advertiser}
//
// Cohort-based monthly view of rejections (settled / cooling / too_new) for
// advertiser × conversion_month. Backend computes maturity + flags; the
// frontend renders without re-computing.
// ============================================================================

export type TRejectionReviewIssueType =
  | 'commercial_impact'
  | 'high_rejection_rate'
  | 'repeated_high_rejection'

// Per-month aggregate row from /rejection-review/trend. Backend computes the
// totals server-side so the chart can render the whole range in a single
// request regardless of how many monthly rows feed into each bucket.
export interface IRejectionReviewTrendRow {
  conversionMonth: string // 'YYYY-MM-01'
  totalRejectedSaleDkk: number
  totalRejectedConversions: number
  totalSaleDkk: number
  totalConversions: number
  flaggedAdvertisers: number
}

export interface IRejectionReviewTrendResponse {
  rangeTotals: IRejectionReviewSummary
  rows: IRejectionReviewTrendRow[]
}

export interface IRejectionReviewSummary {
  commercialImpactAdvertisers: number
  highRejectionRateAdvertisers: number
  repeatedHighRejectionAdvertisers: number
  totalFlaggedAdvertisers: number
  totalRejectedSaleDkk: number
  totalRejectedConversions: number
  flaggedMonths: number
}

export interface IRejectionReviewMonthRow {
  conversionMonth: string // 'YYYY-MM-01'
  advertiserDomain: string
  // Negative = current/future month, still accumulating; positive = past
  // month. Use deriveMaturity() to map to a label/badge.
  daysSinceMonthEnd: number
  totalConversions: number
  rejectedConversions: number
  totalSaleDkk: number
  rejectedSaleDkk: number
  totalCommissionDkk: number
  rejectedCommissionDkk: number
  rejectedConversionRate: number
  rejectedSaleRate: number
  rejectedCommissionRate: number
  issueTypes: TRejectionReviewIssueType[]
  reasonText: string
  networkUuidsContext: string
}

export interface IRejectionReviewListResponse {
  page: number
  pageSize: number
  total: number
  rows: IRejectionReviewMonthRow[]
}

// Per-advertiser drilldown — same shape as list rows minus advertiserDomain.
export type IRejectionReviewAdvertiserMonth = Omit<
  IRejectionReviewMonthRow,
  'advertiserDomain'
>

export interface IRejectionReviewAdvertiserResponse {
  advertiserDomain: string
  months: IRejectionReviewAdvertiserMonth[]
  // Optional — backend may or may not ship a name list yet. When present,
  // the network lane uses these names; otherwise it falls back to short
  // UUIDs from each month's networkUuidsContext.
  networks?: IAnomaliesV2Network[]
}

// /anomalies/v2/rejection-review/advertisers — advertiser-grouped review queue.
// Each row carries its month-level breakdown inline (months[]) so expand is
// instant.
export interface IRejectionReviewAdvertiserQueueRow {
  advertiserDomain: string
  issueTypes: TRejectionReviewIssueType[]
  affectedMonths: number
  totalRejectedSaleDkk: number
  totalRejectedConversions: number
  totalSaleDkk: number
  totalConversions: number
  maxRejectedSaleRate: number | null
  maxRejectedConversionRate: number | null
  worstMonth: string | null
  reasonText: string
  months: IRejectionReviewAdvertiserMonth[]
}

export interface IRejectionReviewAdvertisersResponse {
  page: number
  pageSize: number
  total: number
  rows: IRejectionReviewAdvertiserQueueRow[]
}
